import { NETCODE_V4_CHAIN_DELTA_QUANTIZATION } from '@star-snake/shared'
import { decodeSnakeLengthWire } from '@star-snake/shared'
import type { DeathMessage, EvacuationMessage, SessionOverMessage, ViolationWarningMessage } from '@star-snake/shared'
import { BinaryReaderV4 } from './BinaryReaderV4'

export interface ChainPointV4 {
  x: number
  z: number
}

export interface DecodedAssignSnakeV4 {
  type: number
  entityId: number
  spawnToken: number
  x: number
  z: number
  bearing: number
  length: number
  flags: number
}

export interface DecodedSelfHeadStateV4 {
  type: number
  serverTick: number
  serverMonoMs: number
  spawnToken: number
  ackSeq: number
  headX: number
  headZ: number
  bearing: number
  length: number
  flags: number
}

export interface DecodedSelfChainSnapshotV4 extends DecodedSelfHeadStateV4 {
  segmentCount: number
  points: ChainPointV4[]
}

export interface DecodedRemoteChainV4 {
  entityId: number
  spawnToken: number
  fidelity: number
  flags: number
  botBehavior: number
  length: number
  segmentCount: number
  points: ChainPointV4[]
}

export interface DecodedRemoteChainBatchV4 {
  type: number
  serverTick: number
  serverMonoMs: number
  snakes: DecodedRemoteChainV4[]
}

export interface DecodedPongV4 {
  type: number
  clientMonoMs: number
  serverMonoMs: number
}

export interface DecodedSparkV4 {
  sparkId: number
  x: number
  z: number
  value: number
}

export interface DecodedSparkBatchV4 {
  type: number
  serverTick: number
  serverMonoMs: number
  sparks: DecodedSparkV4[]
}

export interface DecodedSparkRemoveV4 {
  type: number
  serverTick: number
  serverMonoMs: number
  sparkIds: number[]
}

export interface DecodedDeathScatterBundleV4 {
  startX: number
  startZ: number
  targetX: number
  targetZ: number
  value: number
  landingTick: number
  band: number
  seed: number
}

export interface DecodedDeathScatterV4 {
  type: number
  serverTick: number
  serverMonoMs: number
  sourceEntityId: number
  bundles: DecodedDeathScatterBundleV4[]
}

export interface DecodedGameStateV4 {
  type: number
  serverTick: number
  serverMonoMs: number
  timer: number
  maxDuration: number
  phase: string
  runtimeConfig: {
    baseSpeed: number
    baseTurnRate: number
  }
  leaderboard: Array<{ entityId: number; name: string; score: number; isBot: boolean }>
  minimap: {
    snakes: Array<{ entityId: number; x: number; z: number; isBot: boolean }>
    radius: number
  }
  portal: {
    x: number
    z: number
    active: boolean
    radius: number
    relocating: boolean
    timeLeft: number
  }
}

export interface DecodedDeathV4 extends DeathMessage {
  type: number
  serverTick: number
  serverMonoMs: number
  adminKick?: boolean
  adminReason?: string
}

export interface DecodedEvacuationV4 extends EvacuationMessage {
  type: number
  serverTick: number
  serverMonoMs: number
}

export interface DecodedSessionOverV4 extends SessionOverMessage {
  type: number
  serverTick: number
  serverMonoMs: number
}

export interface DecodedGameEndV4 {
  type: number
  serverTick: number
  serverMonoMs: number
}

export interface DecodedViolationWarningV4 extends ViolationWarningMessage {
  type: number
  serverTick: number
  serverMonoMs: number
}

export class BinaryDecoderV4 {
  decodeAssignSnake(data: ArrayBuffer): DecodedAssignSnakeV4 {
    const reader = new BinaryReaderV4(data)
    return {
      type: reader.readUint8(),
      entityId: reader.readUint16(),
      spawnToken: reader.readUint32(),
      x: reader.readFloat32(),
      z: reader.readFloat32(),
      bearing: reader.readFloat32(),
      length: decodeSnakeLengthWire(reader.readUint16()),
      flags: reader.readUint8(),
    }
  }

  decodeSelfHeadState(data: ArrayBuffer): DecodedSelfHeadStateV4 {
    const reader = new BinaryReaderV4(data)
    return {
      type: reader.readUint8(),
      serverTick: reader.readUint32(),
      serverMonoMs: reader.readUint48(),
      spawnToken: reader.readUint32(),
      ackSeq: reader.readUint16(),
      headX: reader.readFloat32(),
      headZ: reader.readFloat32(),
      bearing: reader.readFloat32(),
      length: decodeSnakeLengthWire(reader.readUint16()),
      flags: reader.readUint8(),
    }
  }

  decodeSelfChainSnapshot(data: ArrayBuffer): DecodedSelfChainSnapshotV4 {
    const reader = new BinaryReaderV4(data)
    const type = reader.readUint8()
    const serverTick = reader.readUint32()
    const serverMonoMs = reader.readUint48()
    const spawnToken = reader.readUint32()
    const ackSeq = reader.readUint16()
    const headX = reader.readFloat32()
    const headZ = reader.readFloat32()
    const bearing = reader.readFloat32()
    const length = decodeSnakeLengthWire(reader.readUint16())
    const flags = reader.readUint8()
    const segmentCount = reader.readUint16()
    const headPointX = reader.readFloat32()
    const headPointZ = reader.readFloat32()

    return {
      type,
      serverTick,
      serverMonoMs,
      spawnToken,
      ackSeq,
      headX,
      headZ,
      bearing,
      length,
      flags,
      segmentCount,
      points: this.decodeChainPoints(reader, segmentCount, headPointX, headPointZ),
    }
  }

  decodeRemoteChainBatch(data: ArrayBuffer): DecodedRemoteChainBatchV4 {
    const reader = new BinaryReaderV4(data)
    const type = reader.readUint8()
    const serverTick = reader.readUint32()
    const serverMonoMs = reader.readUint48()
    const snakeCount = reader.readUint16()
    const snakes: DecodedRemoteChainV4[] = []

    for (let i = 0; i < snakeCount; i++) {
      const entityId = reader.readUint16()
      const spawnToken = reader.readUint32()
      const fidelity = reader.readUint8()
      const flags = reader.readUint8()
      const botBehavior = reader.readUint8()
      const length = decodeSnakeLengthWire(reader.readUint16())
      const segmentCount = reader.readUint16()
      const headPointX = reader.readFloat32()
      const headPointZ = reader.readFloat32()

      snakes.push({
        entityId,
        spawnToken,
        fidelity,
        flags,
        botBehavior,
        length,
        segmentCount,
        points: this.decodeChainPoints(reader, segmentCount, headPointX, headPointZ),
      })
    }

    return { type, serverTick, serverMonoMs, snakes }
  }

  decodePong(data: ArrayBuffer): DecodedPongV4 {
    const reader = new BinaryReaderV4(data)
    return {
      type: reader.readUint8(),
      clientMonoMs: reader.readUint32(),
      serverMonoMs: reader.readUint48(),
    }
  }

  decodeSparkBatch(data: ArrayBuffer): DecodedSparkBatchV4 {
    const reader = new BinaryReaderV4(data)
    const type = reader.readUint8()
    const serverTick = reader.readUint32()
    const serverMonoMs = reader.readUint48()
    const sparkCount = reader.readUint16()
    const sparks: DecodedSparkV4[] = []
    for (let i = 0; i < sparkCount; i++) {
      sparks.push({
        sparkId: reader.readUint16(),
        x: reader.readFloat32(),
        z: reader.readFloat32(),
        value: reader.readUint16(),
      })
    }
    return { type, serverTick, serverMonoMs, sparks }
  }

  decodeSparkRemove(data: ArrayBuffer): DecodedSparkRemoveV4 {
    const reader = new BinaryReaderV4(data)
    const type = reader.readUint8()
    const serverTick = reader.readUint32()
    const serverMonoMs = reader.readUint48()
    const sparkCount = reader.readUint16()
    const sparkIds: number[] = []
    for (let i = 0; i < sparkCount; i++) {
      sparkIds.push(reader.readUint16())
    }
    return { type, serverTick, serverMonoMs, sparkIds }
  }

  decodeDeathScatter(data: ArrayBuffer): DecodedDeathScatterV4 {
    const reader = new BinaryReaderV4(data)
    const type = reader.readUint8()
    const serverTick = reader.readUint32()
    const serverMonoMs = reader.readUint48()
    const sourceEntityId = reader.readUint16()
    const bundleCount = reader.readUint16()
    const bundles: DecodedDeathScatterBundleV4[] = []
    for (let i = 0; i < bundleCount; i++) {
      bundles.push({
        startX: reader.readFloat32(),
        startZ: reader.readFloat32(),
        targetX: reader.readFloat32(),
        targetZ: reader.readFloat32(),
        value: reader.readUint16(),
        landingTick: reader.readUint32(),
        band: reader.readUint8(),
        seed: reader.readUint16(),
      })
    }
    return { type, serverTick, serverMonoMs, sourceEntityId, bundles }
  }

  decodeGameState(data: ArrayBuffer): DecodedGameStateV4 {
    const reader = new BinaryReaderV4(data)
    const type = reader.readUint8()
    const serverTick = reader.readUint32()
    const serverMonoMs = reader.readUint48()
    const timer = reader.readFloat32()
    const maxDuration = reader.readFloat32()
    const phase = reader.readString()
    const baseSpeed = reader.readFloat32()
    const baseTurnRate = reader.readFloat32()
    const leaderboardCount = reader.readUint16()
    const leaderboard: DecodedGameStateV4['leaderboard'] = []
    for (let i = 0; i < leaderboardCount; i++) {
      leaderboard.push({
        entityId: reader.readUint16(),
        name: reader.readString(),
        score: reader.readUint16(),
        isBot: reader.readBoolean(),
      })
    }
    const minimapCount = reader.readUint16()
    const snakes: DecodedGameStateV4['minimap']['snakes'] = []
    for (let i = 0; i < minimapCount; i++) {
      snakes.push({
        entityId: reader.readUint16(),
        x: reader.readFloat32(),
        z: reader.readFloat32(),
        isBot: reader.readBoolean(),
      })
    }
    const radius = reader.readFloat32()
    const portal = {
      x: reader.readFloat32(),
      z: reader.readFloat32(),
      active: reader.readBoolean(),
      radius: reader.readFloat32(),
      relocating: reader.readBoolean(),
      timeLeft: reader.readFloat32(),
    }

    return {
      type,
      serverTick,
      serverMonoMs,
      timer,
      maxDuration,
      phase,
      runtimeConfig: { baseSpeed, baseTurnRate },
      leaderboard,
      minimap: { snakes, radius },
      portal,
    }
  }

  decodeDeath(data: ArrayBuffer): DecodedDeathV4 {
    const reader = new BinaryReaderV4(data)
    const type = reader.readUint8()
    const serverTick = reader.readUint32()
    const serverMonoMs = reader.readUint48()
    const reason = reader.readString() as DeathMessage['reason']
    const hasKillerId = reader.readBoolean()
    const killedBy = hasKillerId ? reader.readString() : null
    const hasKillerName = reader.readBoolean()
    const killedByName = hasKillerName ? reader.readString() : null
    const sparksDropped = reader.readUint16()
    const respawnCost = reader.readUint16()
    const canRespawn = reader.readBoolean()
    const respawnsLeft = reader.readUint8()
    const adminKick = reader.readBoolean()
    const adminReason = adminKick ? reader.readString() : undefined
    return {
      type,
      serverTick,
      serverMonoMs,
      reason,
      killedBy,
      killedByName,
      sparksDropped,
      respawnCost,
      canRespawn,
      respawnsLeft,
      adminKick,
      adminReason,
    }
  }

  decodeEvacuation(data: ArrayBuffer): DecodedEvacuationV4 {
    const reader = new BinaryReaderV4(data)
    return {
      type: reader.readUint8(),
      serverTick: reader.readUint32(),
      serverMonoMs: reader.readUint48(),
      sparksCollected: reader.readUint16(),
      starsEarned: reader.readUint16(),
      profit: reader.readInt16(),
      snakeLength: reader.readUint16(),
    }
  }

  decodeSessionOver(data: ArrayBuffer): DecodedSessionOverV4 {
    const reader = new BinaryReaderV4(data)
    const type = reader.readUint8()
    const serverTick = reader.readUint32()
    const serverMonoMs = reader.readUint48()
    const statCount = reader.readUint16()
    const stats: SessionOverMessage['stats'] = []
    for (let i = 0; i < statCount; i++) {
      stats.push({
        name: reader.readString(),
        kills: reader.readUint16(),
        maxLength: reader.readUint16(),
        sparksCollected: reader.readUint16(),
        timeAlive: reader.readUint16(),
        evacuated: reader.readBoolean(),
      })
    }
    const sessionDuration = reader.readUint16()
    return { type, serverTick, serverMonoMs, stats, sessionDuration }
  }

  decodeGameEnd(data: ArrayBuffer): DecodedGameEndV4 {
    const reader = new BinaryReaderV4(data)
    return {
      type: reader.readUint8(),
      serverTick: reader.readUint32(),
      serverMonoMs: reader.readUint48(),
    }
  }

  decodeViolationWarning(data: ArrayBuffer): DecodedViolationWarningV4 {
    const reader = new BinaryReaderV4(data)
    return {
      type: reader.readUint8(),
      serverTick: reader.readUint32(),
      serverMonoMs: reader.readUint48(),
      count: reader.readUint16(),
      max: reader.readUint16(),
      reason: reader.readString(),
    }
  }

  private decodeChainPoints(
    reader: BinaryReaderV4,
    segmentCount: number,
    headPointX: number,
    headPointZ: number,
  ): ChainPointV4[] {
    if (segmentCount <= 0) return []

    const points: ChainPointV4[] = [{ x: headPointX, z: headPointZ }]
    let currentX = headPointX
    let currentZ = headPointZ

    for (let i = 1; i < segmentCount; i++) {
      currentX += reader.readInt16() * NETCODE_V4_CHAIN_DELTA_QUANTIZATION
      currentZ += reader.readInt16() * NETCODE_V4_CHAIN_DELTA_QUANTIZATION
      points.push({ x: currentX, z: currentZ })
    }

    return points
  }
}
